import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRoute } from 'wouter';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdAlert } from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import localforage from 'localforage';
import { useQueries } from '@tanstack/react-query';
import Driving from './Driving';
import Stopped from './Stopped';
import { IWidgetGraph } from '../../OnlinePanel/OnlinePanelState';
import { IVehicleData } from '../../Vehicles/types';
import SelectRidesModal from '../SelectRidesModal';
import { URL_GRAPHS, URL_VELOCITY } from '../../../router/constants';
import {
  getData,
  getDataByRideId,
  getVehicleName,
  getIsCompressed,
  IGraphResponseData,
  getPartData,
  getPartDataByRideId,
  getDateFrom,
  getDateTo,
  getRideStartDateAndAddress,
  getRideEndDateAndAddress,
  getVehicleSpeedLimits,
} from '../GraphService';
import RideStartAndEnd from '../RideStartAndEnd';
import PartSelecting from '../PartSelecting';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import PositionDetails from '../PositionsDetails';
import {
  isGraphOpenAtom,
  lastClickedTimestampAtom,
  speedWidgetGraphAtom,
  widgetGraphSelectedValuesAtom,
} from '../GraphState';
import { setLoaderAtom } from '../../../store/recoil/AppState';
import { vehicleDataByIdSelector } from '../../../store/recoil/vehiclesData';
import { vehicleByIdSelector } from '../../../store/recoil/vehicles';
import useAnalytics from '../../../hooks/useAnalytics';

interface IVelocityGraphProps {
  vehicleId?: number;
}

export default function VelocityOnline(props: IVelocityGraphProps) {
  const { trackEvent } = useAnalytics();
  const [, params] = useRoute(`${URL_GRAPHS}${URL_VELOCITY}/:vehicleId`);
  const { formatMessage: f } = useIntl();

  const [speedLimits, setSpeedLimits] = React.useState<number[] | null>(null);
  const setWidgetGraphSelectedValues = useSetRecoilState<string[] | null>(
    widgetGraphSelectedValuesAtom
  );
  const [defaultDates, setDefaultDates] = React.useState<Date[] | undefined>(undefined);
  const vehicleData = useRecoilValue<IVehicleData | null>(
    vehicleDataByIdSelector(props.vehicleId || Number(params?.vehicleId) || 0)
  );
  const setLastClickedTimestamp = useSetRecoilState<string>(lastClickedTimestampAtom);
  const [startDateAndAddress, setStartDateAndAddress] = React.useState('');
  const [lastDateAndAddress, setLastDateAndAddress] = React.useState('');
  const [endRideDateAndAddress, setEndDateAndAddress] = React.useState('');
  const [showSelectRidesModal, setShowSelectRidesModal] = React.useState(false);
  const [rideId, setRideId] = React.useState('');
  const [responseData, setResponseData] = React.useState<IGraphResponseData[]>([]);
  const setLoader = useSetRecoilState<boolean>(setLoaderAtom);
  const [speedWidgetGraph, setSpeedWidgetGraph] = useRecoilState<IWidgetGraph | null>(
    speedWidgetGraphAtom
  );
  const [origStatus, setOrigStatus] = React.useState('');
  const [isOrigCompressed, setIsOrigCompressed] = React.useState(false);
  const { setDocumentTitle } = useDocumentTitle();
  const vehicle = useRecoilValue(
    vehicleByIdSelector(
      props.vehicleId || (params && params.vehicleId ? Number(params.vehicleId) : false)
    )
  );
  const setIsGraphOpen = useSetRecoilState<boolean | number>(isGraphOpenAtom);

  React.useEffect(() => {
    if (vehicle && params?.vehicleId) {
      const documentTitle = `${f({ id: 'graph.velocity.page.title' })} ${getVehicleName(vehicle)}`;
      setDocumentTitle(documentTitle);
    }
  }, [f, params, setDocumentTitle, vehicle]);

  React.useEffect(() => {
    if (vehicle) {
      setIsGraphOpen(vehicle.id);
    }
  }, [setIsGraphOpen, vehicle]);

  const resetGraphStates = () => {
    setResponseData([]);
    setStartDateAndAddress('');
    setLastDateAndAddress('');
    setEndDateAndAddress('');
    setIsOrigCompressed(false);
    setOrigStatus(rideId ? 'STOP' : '');
    setLastClickedTimestamp('');
  };

  const handleData = async () => {
    if (vehicle) {
      setLoader(true);
      const response = await getData('speed', String(vehicle.id));
      if (response) {
        if (speedWidgetGraph) {
          setSpeedWidgetGraph((oldState: any) => {
            return { ...oldState, timestamp: response[0]?.timestamp };
          });
        }
        if (vehicleData?.status === 'STOP') {
          localforage.setItem('widgetGraph', {
            vehicleId: vehicle.id,
            data: response,
            name: 'speed',
            rideId: null,
          });
        } else {
          localforage.setItem('widgetGraph', null);
        }
        setResponseData(response);
        if (params) {
          setLastClickedTimestamp(response[response.length - 1]?.timestamp);
        }
        setIsOrigCompressed(getIsCompressed(response));
        setStartDateAndAddress(getRideStartDateAndAddress(response));
        setLastDateAndAddress(getRideEndDateAndAddress(response));
      } else if (!params && speedWidgetGraph) {
        setSpeedWidgetGraph((oldState: any) => {
          return { ...oldState, timestamp: '' };
        });
      } else {
        resetGraphStates();
      }
      setLoader(false);
    }
  };

  const handleDataByRideId = async (id: string): Promise<void> => {
    if (vehicle) {
      setLoader(true);
      const response = await getDataByRideId('speed', String(vehicle.id), id);
      if (response) {
        setResponseData(response);
        setLastClickedTimestamp(response[response.length - 1]?.timestamp);
        setRideId(id);
        setIsOrigCompressed(getIsCompressed(response));
        localforage.setItem('widgetGraph', {
          vehicleId: vehicle.id,
          data: response,
          name: 'speed',
          rideId: id,
        });
        setStartDateAndAddress(getRideStartDateAndAddress(response));
        setLastDateAndAddress(getRideEndDateAndAddress(response));
      } else {
        resetGraphStates();
      }
      setLoader(false);
    }
  };

  const getDataFromLocalforage = async () => {
    const data: IWidgetGraph | null = await localforage.getItem('widgetGraph');
    if (
      data &&
      data.data &&
      vehicle &&
      data.vehicleId === vehicle.id &&
      data.name === 'speed' &&
      !data.rideId
    ) {
      setResponseData(data.data);
      if (params) {
        setLastClickedTimestamp(data.data[data.data.length - 1]?.timestamp);
      }
      setIsOrigCompressed(getIsCompressed(data.data));
      setStartDateAndAddress(getRideStartDateAndAddress(data.data));
      setLastDateAndAddress(getRideEndDateAndAddress(data.data));
    } else {
      handleData();
    }
  };

  const handleSpeedLimits = async () => {
    const data = await getVehicleSpeedLimits();
    if (data && data.length === 3) {
      setSpeedLimits(data);
      localforage.setItem('speedLimits', data);
    }
  };

  const getSpeedLimitsFromLocalforage = async () => {
    const data: number[] | null = await localforage.getItem('speedLimits');
    if (data && data.length === 3) {
      setSpeedLimits(data);
    } else {
      handleSpeedLimits();
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['velocityData', vehicle?.id],
        queryFn: () => getDataFromLocalforage(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!(params || (!params && speedWidgetGraph && !speedWidgetGraph.timestamp)),
      },
      {
        queryKey: ['velocitySpeedLimits'],
        queryFn: () => getSpeedLimitsFromLocalforage(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: true,
      },
    ],
  });

  React.useEffect(() => {
    if (vehicleData?.status && !origStatus) {
      setOrigStatus(vehicleData.status);
    }
  }, [origStatus, vehicleData?.status]);

  const handleModal = () => {
    trackEvent([
      'Velocity graph page',
      'click',
      showSelectRidesModal ? 'Close select ride modal' : 'Open select ride modal',
    ]);
    setShowSelectRidesModal(!showSelectRidesModal);
  };

  const handleRide = (id: string, dates: Date[]) => {
    trackEvent(['Velocity graph page', 'click', 'Select ride']);
    setWidgetGraphSelectedValues(null);
    handleModal();
    handleDataByRideId(id);
    setDefaultDates(dates);
  };

  const handleEndRideDateAndAddress = (address: string) => {
    setEndDateAndAddress(address);
  };

  const handleDataFromNewRide = () => {
    setOrigStatus('DRIVING');
    setRideId('');
    handleData();
  };

  const handleDataAfterFinishedRide = () => {
    setOrigStatus('STOP');
    handleData();
  };

  const renderGraph = () => {
    if (!origStatus) {
      return <div />;
    }
    const isCompressed = getIsCompressed(responseData);
    if (vehicle) {
      return origStatus === 'STOP' || rideId ? (
        <Stopped
          responseData={responseData}
          isCompressed={isCompressed}
          loadData={handleDataFromNewRide}
          vehicleId={vehicle.id}
          isSelectable={isCompressed && !!params}
          speedLimits={speedLimits}
        />
      ) : (
        <Driving
          vehicleId={vehicle.id}
          responseData={responseData}
          isCompressed={isCompressed}
          loadRide={handleDataAfterFinishedRide}
          handleEndRideDateAndAddress={handleEndRideDateAndAddress}
          speedLimits={speedLimits}
          isDetail={!!params}
        />
      );
    }
    return null;
  };

  const loadPartData = async (dateFrom: Date, dateTo: Date): Promise<void> => {
    setLoader(true);
    if (vehicle) {
      const response = !rideId
        ? await getPartData('speed', String(vehicle.id), [dateFrom, dateTo])
        : await getPartDataByRideId('speed', String(vehicle.id), rideId, [dateFrom, dateTo]);
      if (response) {
        setResponseData(response);
        setLastClickedTimestamp(response[response.length - 1]?.timestamp);
      }
      setLoader(false);
    }
  };

  const getDataByRideIdFromLocalforage = async (id: string) => {
    const data: IWidgetGraph | null = await localforage.getItem('widgetGraph');
    if (
      data &&
      data.data &&
      vehicle &&
      data.vehicleId === vehicle.id &&
      data.name === 'speed' &&
      data.rideId === id
    ) {
      setResponseData(data.data);
      if (params) {
        setLastClickedTimestamp(data.data[data.data.length - 1]?.timestamp);
      }
    } else {
      handleDataByRideId(id);
    }
  };

  const resetPartData = () => {
    if (rideId) {
      getDataByRideIdFromLocalforage(rideId);
    } else {
      getDataFromLocalforage();
    }
  };

  const renderCompressedGraphAlert = () => {
    return getIsCompressed(responseData) ? (
      <div style={{ padding: params ? '20px 0 0 0' : '' }}>
        <CmdAlert
          id="velocity-graph-alert"
          data={{
            title: f({ id: 'graph.velocity.alert.title' }),
            message: params
              ? f({ id: 'graph.velocity.alert.message' })
              : f({ id: 'graph.onlinepanel.alert.message' }),
            type: 'info',
          }}
          noCancel
        />
      </div>
    ) : null;
  };

  return (
    <div
      className="container-fluid"
      data-cy="velocity-graph"
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {showSelectRidesModal && vehicle && (
        <SelectRidesModal
          vehicleId={String(vehicle.id)}
          closeCallback={handleModal}
          handleRide={handleRide}
          defaultRideId={rideId}
          defaultDates={defaultDates}
        />
      )}
      {params && vehicle ? (
        [
          <div className="row section page-head" data-cy="reports-head">
            <h4 data-cy="reports-title">
              {`
                 ${f({
                   id: 'graph.velocity.page.title',
                 })}`}{' '}
              <span className="text-gray-light">{getVehicleName(vehicle)}</span>
            </h4>
          </div>,
          <CmdBox
            id="velocity-basic-information"
            title={f({ id: 'graph.velocity.section.overview' })}
          >
            <RideStartAndEnd
              rideId={rideId}
              handleModal={handleModal}
              origStatus={origStatus}
              startDateAndAddress={startDateAndAddress}
              lastDateAndAddress={lastDateAndAddress}
              endDateAndAddress={endRideDateAndAddress}
            />
            {isOrigCompressed && (origStatus === 'STOP' || rideId) && (
              <PartSelecting
                dateFrom={getDateFrom(responseData)}
                dateTo={getDateTo(responseData)}
                loadPartData={loadPartData}
                loadData={resetPartData}
                data={responseData}
                rideId={rideId}
              />
            )}
            {renderCompressedGraphAlert()}
            <div style={{ marginTop: '10px', padding: '20px 0px 20px 25px' }}>{renderGraph()}</div>
          </CmdBox>,
          <PositionDetails vehicleId={vehicle.id} positions={responseData} />,
        ]
      ) : (
        <div>
          {renderCompressedGraphAlert()}
          {renderGraph()}
        </div>
      )}
    </div>
  );
}
